import { useEffect, useState } from "react"
import { cn } from "@vc/lib/utils"
import { Radio, Square, Eye, EyeOff, TrendingUp, AlertCircle, X, Check } from "lucide-react"
import { Button } from "@/clientWeb/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/clientWeb/components/ui/vcDialog"
import { Input } from "@/clientWeb/components/ui/input"
import { Label } from "@/clientWeb/components/ui/label"
import { useResponsiveDevice } from "@vc/viewmodels/hooks/useResponsiveDevice"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"

/* ─── Types ─────────────────────────────────────────────────────── */
interface LiveStreamingControlsProps {
  isStreaming?: boolean
  viewerCount?: number
  streamDuration?: number
  streamHealth?: "excellent" | "good" | "fair" | "poor"
  onStartStream?: (config: StreamConfig) => void
  onStopStream?: () => void
  className?: string
  showDialog?: boolean
  onShowDialogChange?: (open: boolean) => void
}

interface StreamPlatform {
  id: string
  platform: PlatformId
  streamKey: string
  streamUrl?: string
  enabled?: boolean
}

interface StreamConfig {
  platforms: StreamPlatform[]
  title: string
}

type PlatformId = "YouTube" | "Facebook" | "Twitch" | "LinkedIn" | "Custom"

/* ─── Platform definitions ───────────────────────────────────────── */
const PLATFORMS: {
  id: PlatformId
  label: string
  sublabel: string
  color: string        // brand bg color
  textColor: string    // icon / text color
  ringColor: string    // selected ring
  icon: React.ReactNode
}[] = [
    {
      id: "YouTube",
      label: "YouTube",
      sublabel: "Live",
      color: "#FEE2E2",
      textColor: "#DC2626",
      ringColor: "#e38282",
      icon: (
        /* YouTube play icon SVG — exact brand shape */
        <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
        </svg>
      ),
    },
    {
      id: "Facebook",
      label: "Facebook",
      sublabel: "Live",
      color: "#DBEAFE",
      textColor: "#1D4ED8",
      ringColor: "#5f7fb4",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
      ),
    },
    // ── Twitch and LinkedIn: parked, not deleted ──────────────────────
    // Neither has a working destination behind it. LinkedIn issues a
    // per-broadcast RTMP URL rather than a stable ingest, so it can only ever
    // go through Custom; Twitch has a stable ingest but it has never been
    // tested against a real stream. Offering either would let a host enter a
    // key, go "live", and find nothing arrived.
    //
    // The definitions are kept verbatim so re-enabling is uncommenting. The
    // backend still refuses both by name (NEEDS_CUSTOM_URL in
    // streaming-control.router.ts), which stays as the backstop.
    // {
    // id: "Twitch",
    // label: "Twitch",
    // sublabel: "Stream",
    // color: "#EDE9FE",
    // textColor: "#7C3AED",
    // ringColor: "#977cd5",
    // icon: (
    // <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
    // <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    // </svg>
    // ),
    // },
    // {
    // id: "LinkedIn",
    // label: "LinkedIn",
    // sublabel: "Live",
    // color: "#DBEAFE",
    // textColor: "#1E40AF",
    // ringColor: "#667ebf",
    // icon: (
    // <svg viewBox="0 0 24 24" fill="currentColor" width="26" height="26">
    // <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    // </svg>
    // ),
    // },
  ]

/* ─── Platform Selector Row ──────────────────────────────────────── */
function PlatformSelector({
  selected,
  onChange,
}: {
  selected: PlatformId
  onChange: (id: PlatformId) => void
}) {
  return (
    <div className="grid grid-cols-4 pt-2 gap-3 sm:gap-4">
      {PLATFORMS.map((p) => {
        const isActive = selected === p.id
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onChange(p.id)}
            style={{
              /* brand color ring on selection */
              outline: isActive ? `2.5px solid ${p.ringColor}` : "2.5px solid transparent",
              outlineOffset: "2px",
              transition: "all 0.18s cubic-bezier(0.22,1,0.36,1)",
            }}
            className={cn(
              "relative flex mt-2 flex-col items-center gap-1.5 rounded-2xl border py-2 cursor-pointer select-none",
              "focus-visible:ring-2 focus-visible:ring-offset-2",
              isActive
                ? "border-transparent shadow-md scale-[1.04]"
                : "border-slate-200 dark:border-border bg-card hover:border-slate-300 dark:hover:border-border hover:bg-slate-50 dark:hover:bg-muted hover:scale-[1.02]",
            )}
          >
            {/* Brand icon bubble */}
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl transition-all duration-150"
              style={{
                // background: isActive ? p.color : "#F8FAFC",
                color: p.textColor,
                // boxShadow: isActive ? `0 4px 12px ${p.ringColor}30` : "none",
              }}
            >
              {p.icon}
            </div>

            {/* Label */}
            <div className="text-center leading-none">
              <p className={cn(
                "text-[11px] font-700 leading-tight",
                isActive ? "text-slate-900 dark:text-foreground/80" : "text-slate-600 dark:text-muted-foreground",
              )}
                style={{ fontWeight: isActive ? 700 : 500 }}
              >
                {p.label}
              </p>
              {/* <p className="text-[9px] text-slate-400 mt-0.5">{p.sublabel}</p> */}
            </div>

            {/* Selected checkmark */}
            {isActive && (
              <div
                className="absolute -top-1.5 -right-1.5 flex h-4 w-4 items-center justify-center rounded-full text-white shadow-sm"
                style={{ background: p.ringColor }}
              >
                <Check className="h-2.5 w-2.5" strokeWidth={3} />
              </div>
            )}
          </button>
        )
      })}
    </div>
  )
}

/* ─── Helpers ────────────────────────────────────────────────────── */
function formatDuration(seconds: number) {
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = seconds % 60
  return [h, m, s].map((v) => String(v).padStart(2, "0")).join(":")
}

function getPlatformMeta(id: PlatformId) {
  return PLATFORMS.find((p) => p.id === id) ?? PLATFORMS[0]
}

function emptyStreamConfig(): StreamConfig {
  return {
    platforms: [{ id: "1", platform: "YouTube", streamKey: "", enabled: true }],
    title: "",
  }
}

/**
 * Keeps password managers out of the dialog.
 *
 * A text input followed by a `type="password"` one reads to Chrome and Edge as
 * a login form: they offered to save the title as a username and the stream key
 * as its password, then autofilled both into every later dialog — in any
 * meeting, long after the broadcast ended. The key field is therefore a plain
 * text input masked with CSS, which no password manager treats as a credential.
 */
const NO_AUTOFILL = {
  autoComplete: "off",
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: false,
  "data-lpignore": "true",
  "data-1p-ignore": "true",
  "data-bwignore": "true",
  "data-form-type": "other",
} as const

/* ─── Main Component ─────────────────────────────────────────────── */
export function LiveStreamingControls({
  isStreaming = false,
  viewerCount = 0,
  onStartStream,
  onStopStream,
  showDialog: externalShowDialog,
  onShowDialogChange,
}: LiveStreamingControlsProps) {
  const t = useTranslation()
  const [internalShowDialog, setInternalShowDialog] = useState(false)
  const showDialog = externalShowDialog !== undefined ? externalShowDialog : internalShowDialog
  const setShowDialog = externalShowDialog !== undefined
    ? (open: boolean) => onShowDialogChange?.(open)
    : setInternalShowDialog

  const [showStopDialog, setShowStopDialog] = useState(false)
  const [streamConfig, setStreamConfig] = useState<StreamConfig>(emptyStreamConfig)
  const [revealKey, setRevealKey] = useState(false)

  const { isMobileDevice, isLandscape } = useResponsiveDevice()
  const isPhoneLandscape = isMobileDevice && isLandscape

  const selectedPlatforms = streamConfig.platforms.filter((p) => p.enabled !== false)

  const handleStartStream = () => {
    onStartStream?.({ ...streamConfig, platforms: selectedPlatforms })
    setShowDialog(false)
  }

  const handleStopStream = () => {
    onStopStream?.()
    setShowStopDialog(false)
  }

  /**
   * Every time the dialog opens, it opens blank.
   *
   * The form is cleared whenever the dialog closes — Cancel, the X, Escape, an
   * outside click, or Go Live (whose config has already been handed off by
   * then). A key is a credential, and one typed and then abandoned should not
   * sit in memory waiting to reappear; one that was used is normally spent by
   * that broadcast, so offering it again only invites a refused go-live.
   *
   * This also covers a broadcast ending: the dialog closed on Go Live, so the
   * form was already cleared before the stream stopped.
   */
  useEffect(() => {
    if (showDialog) return
    setStreamConfig(emptyStreamConfig())
    setRevealKey(false)
  }, [showDialog])

  const updatePlatform = (id: string, updates: Partial<StreamPlatform>) =>
    setStreamConfig((c) => ({
      ...c,
      platforms: c.platforms.map((p) => {
        if (p.id !== id) return p
        // Switching platform clears the key with it. Keys are per-platform, so
        // carrying one across meant the YouTube key sat in the box under a
        // Facebook heading, looking like it belonged there — and it would have
        // been sent to Facebook's ingest, which can only refuse it.
        const switched = updates.platform !== undefined && updates.platform !== p.platform
        return switched ? { ...p, ...updates, streamKey: "" } : { ...p, ...updates }
      }),
    }))

  const removeSecondPlatform = () =>
    setStreamConfig((c) => ({ ...c, platforms: c.platforms.slice(0, 1) }))

  const togglePlatformEnabled = (id: string) => {
    if (streamConfig.platforms.length <= 1) return
    setStreamConfig((c) => ({
      ...c,
      platforms: c.platforms.map((p) =>
        p.id === id ? { ...p, enabled: p.enabled === false ? true : false } : p,
      ),
    }))
  }

  /* ── Stop stream dialog ── */
  if (isStreaming) {
    return (
      <Dialog open={showStopDialog} onOpenChange={setShowStopDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t("controlBar.stopLiveStream")}</DialogTitle>
            <DialogDescription>
              {t("controlBar.stopStreamWarning")} {viewerCount} {t("controlBar.viewers")}. {t("controlBar.cannotBeUndone")}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStopDialog(false)}>{t("controlBar.cancel")}</Button>
            <Button variant="destructive" onClick={handleStopStream} className="bg-red-600 hover:bg-red-700">
              <Square className="mr-2 h-4 w-4 fill-current" />
              {t("controlBar.stopStream")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  /* ── Start stream dialog ── */
  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent
        maxWidthClass="max-w-[580px]"
        className="overflow-hidden p-0 sm:rounded-2xl"
      >
        <div className={cn("flex flex-col", isPhoneLandscape ? "max-h-[94vh]" : "max-h-[85vh]")}>

          {/* ── Header ─────────────────────────────────────────── */}
          <div className="flex items-center gap-3 border-b px-5 py-4">
            <div className="grid h-11 w-11 place-items-center rounded-2xl bg-red-50 ring-1 ring-red-200">
              <Radio className="h-5 w-5 text-red-500" />
            </div>
            <div>
              <h2 className="text-base font-bold leading-tight">{t("controlBar.startLiveStream")}</h2>
              <p className="text-xs text-muted-foreground mt-0.5">{t("controlBar.configureAndBroadcast")}</p>
            </div>
          </div>

          {/* ── Scrollable body ─────────────────────────────────── */}
          <div className="flex-1 overflow-y-auto px-2 py-2 space-y-6">

            {/* Stream title */}
            <div className="space-y-1.5">
              <Label htmlFor="stream-title" className="text-sm font-semibold">{t("controlBar.streamTitle")}</Label>
              <Input
                id="stream-title"
                placeholder="e.g. Weekly Team Sync — March 2026"
                value={streamConfig.title}
                onChange={(e) => setStreamConfig((c) => ({ ...c, title: e.target.value }))}
                className="h-10 mt-2"
                {...NO_AUTOFILL}
              />
            </div>

            {/* Platform sections */}
            {streamConfig.platforms.map((platform, index) => {
              const meta = getPlatformMeta(platform.platform)
              return (
                <div
                  key={platform.id}
                  className="rounded-2xl border bg-slate-50/60 dark:bg-muted/60 p-4 space-y-4"
                >
                  {/* Section header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-white">
                        {index + 1}
                      </div>
                      <span className="text-sm font-semibold text-slate-800 dark:text-foreground">
                        {index === 0 ? t("controlBar.primaryPlatform") : t("controlBar.secondaryPlatform")}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      {/* Included / excluded toggle — only show for secondary */}
                      {streamConfig.platforms.length > 1 && (
                        <button
                          type="button"
                          onClick={() => togglePlatformEnabled(platform.id)}
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all",
                            platform.enabled !== false
                              ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                              : "border-slate-200 dark:border-border bg-card text-slate-500 dark:text-muted-foreground",
                          )}
                        >
                          <span className={cn(
                            "flex h-3.5 w-3.5 items-center justify-center rounded-full border",
                            platform.enabled !== false
                              ? "border-emerald-600 bg-emerald-600 text-white"
                              : "border-slate-300 bg-card",
                          )}>
                            {platform.enabled !== false && <Check className="h-2 w-2" strokeWidth={3} />}
                          </span>
                          {platform.enabled !== false ? t("controlBar.included") : t("controlBar.excluded")}
                        </button>
                      )}

                      {/* Remove secondary button */}
                      {index === 1 && (
                        <button
                          type="button"
                          onClick={removeSecondPlatform}
                          className="flex h-6 w-6 items-center justify-center rounded-full text-slate-400 dark:text-muted-foreground hover:bg-red-50 dark:hover:bg-red-900/30 hover:text-red-500 transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* ── Platform icon selector row ── */}
                  <div className="space-y-2">
                    <span className="text-xs font-medium text-slate-500 dark:text-muted-foreground">
                      {t("controlBar.selectPlatform")}
                    </span>
                    <PlatformSelector
                      selected={platform.platform}
                      onChange={(id) => updatePlatform(platform.id, { platform: id })}
                    />
                  </div>

                  {/* Stream key */}
                  <div className="space-y-1.5">
                    <div className="flex py-2 items-center justify-between">
                      <Label className="text-xs font-medium text-slate-500 dark:text-muted-foreground tracking-wide">
                        {t("controlBar.streamKey")}
                      </Label>
                      <a
                        href="#"
                        className="text-[11px] font-medium text-primary hover:underline"
                        onClick={(e) => e.preventDefault()}
                      >
                        Get key from {meta.label} →
                      </a>
                    </div>
                    <div className="relative mt-2">
                      <Input
                        type="text"
                        placeholder={t("controlBar.pasteStreamKey")}
                        value={platform.streamKey}
                        onChange={(e) => updatePlatform(platform.id, { streamKey: e.target.value })}
                        className="h-10 text-sm pr-10"
                        style={revealKey ? undefined : ({ WebkitTextSecurity: "disc" } as React.CSSProperties)}
                        {...NO_AUTOFILL}
                      />
                      <button
                        type="button"
                        onClick={() => setRevealKey((v) => !v)}
                        aria-label={revealKey ? "Hide stream key" : "Show stream key"}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground dark:text-muted-foreground/80 cursor-pointer hover:text-foreground transition-colors duration-200"
                      >
                        {revealKey ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  {/* Custom RTMP URL */}
                  {platform.platform === "Custom" && (
                    <div className="space-y-1.5">
                      <Label className="text-xs font-medium text-slate-500 dark:text-muted-foreground uppercase tracking-wide">
                        {t("controlBar.rtmpServerUrl")}
                      </Label>
                      <Input
                        placeholder="rtmp://your-server.com/live"
                        value={platform.streamUrl ?? ""}
                        onChange={(e) => updatePlatform(platform.id, { streamUrl: e.target.value })}
                        className="h-10 text-sm"
                      />
                    </div>
                  )}
                </div>
              )
            })}

            {/* No "add second platform" here on purpose.
             *
             * The mixer publishes to ONE RTMP endpoint — the compositor refuses
             * a second output outright — and only platforms[0] was ever sent.
             * So the button added a slot that silently did nothing: the host
             * filled in a second key and believed they were live on both.
             *
             * The state below is still an array and the multi-platform chips
             * still render for length > 1, so restoring this is one button once
             * the mixer can carry two destinations. */}

            {/* Simulcast summary chips */}
            {streamConfig.platforms.length > 1 && (
              <div className="rounded-xl border border-slate-200 dark:border-border bg-card p-3.5 space-y-2">
                <p className="text-xs font-semibold text-slate-700 dark:text-foreground">{t("controlBar.goingLiveOn")}</p>
                <div className="flex flex-wrap gap-2">
                  {streamConfig.platforms.map((p) => {
                    const m = getPlatformMeta(p.platform)
                    const active = p.enabled !== false
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => togglePlatformEnabled(p.id)}
                        className={cn(
                          "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                          active
                            ? "border-primary/20 bg-primary/8 text-primary"
                            : "border-slate-200 dark:border-border bg-card text-slate-400 dark:text-muted-foreground",
                        )}
                      >
                        <div style={{ color: active ? m.textColor : "#94a3b8" }}>{m.icon}</div>
                        <span>{m.label}</span>
                        {!active && <span className="text-slate-300 dark:text-muted-foreground">(off)</span>}
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Info box */}
            <div className="rounded-xl bg-blue-50 dark:ring-border dark:bg-card p-3.5 ring-1 ring-blue-100">
              <div className="flex gap-2.5">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                <div className="space-y-1">
                  <p className="text-xs font-semibold text-primary">{t("controlBar.beforeYouGoLive")}</p>
                  <ul className="space-y-0.5 text-xs text-primary">
                    <li>• {t("controlBar.ensurePermission")}</li>
                    <li>• {t("controlBar.minimumUploadSpeed")}</li>
                    {streamConfig.platforms.length > 1 && (
                      <li>• {t("controlBar.simulcastBandwidth")}</li>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* ── Footer ─────────────────────────────────────────── */}
          <div className="flex flex-col-reverse gap-2 border-t bg-slate-50/80 dark:bg-muted/80 px-5 py-4 sm:flex-row sm:justify-end">
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              {t("controlBar.cancel")}
            </Button>
            <Button
              onClick={handleStartStream}
              disabled={
                !streamConfig.title ||
                selectedPlatforms.length === 0 ||
                selectedPlatforms.some(
                  (p) => !p.streamKey || (p.platform === "Custom" && !p.streamUrl),
                )
              }
              className="gap-2 bg-red-600 hover:bg-red-700 text-white"
            >
              <Radio className="h-4 w-4" />
              {selectedPlatforms.length > 1
                ? `${t("controlBar.goLiveOn")} ${selectedPlatforms.length} ${t("controlBar.platforms")}`
                : t("controlBar.goLive")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

/* ─── Status bar (unchanged, kept for completeness) ─────────────── */
export function LiveStreamingStatusBar({
  viewerCount = 0,
  streamDuration = 0,
  bitrate = 2500,
  className,
}: {
  viewerCount?: number
  streamDuration?: number
  bitrate?: number
  className?: string
}) {
  return (
    <div className={cn(
      "flex items-center gap-4 rounded-lg bg-black/60 px-4 py-2 backdrop-blur-md ring-1 ring-white/10",
      className,
    )}>
      <div className="flex items-center gap-2">
        <div className="relative">
          <div className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
          <div className="absolute inset-0 h-2 w-2 rounded-full bg-red-500 animate-ping" />
        </div>
        <span className="text-xs font-bold uppercase tracking-wide text-red-400">Live</span>
      </div>

      <div className="h-4 w-px bg-card/20" />

      <div className="flex items-center gap-1.5">
        <Eye className="h-3.5 w-3.5 text-white/70" />
        <span className="text-xs font-semibold text-white">{viewerCount.toLocaleString()}</span>
      </div>

      <div className="h-4 w-px bg-card/20" />

      <span className="text-xs text-white/80">{formatDuration(streamDuration)}</span>

      <div className="h-4 w-px bg-card/20" />

      <div className="flex items-center gap-1.5">
        <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
        <span className="text-xs font-medium text-white/80">{bitrate} kbps</span>
      </div>
    </div>
  )
}